import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { Badge, type BadgeVariant } from '@/components/Badge';
import { formatDate } from '@/utils/date';
import type {
    AccountBinding,
    LicenseState,
    LicenseStatus,
} from '@/types/license';
import { useLicenseStatus } from '../hooks/useLicenseStatus';
import { AwsConnectivityRows } from './AwsConnectivityRows';
import { DetailRow } from './DetailRow';
import { FingerprintRow } from './FingerprintRow';
import { LicenseUpload } from './LicenseUpload';
import { ModelServicesSection } from './ModelServicesSection';
import { UnlicensedNotice } from './UnlicensedNotice';
import { formatFingerprint } from './utils';

const STATUS_VARIANTS: Record<LicenseState, BadgeVariant> = {
    valid: 'positive',
    expired: 'negative',
};

const BINDING_VARIANTS: Record<AccountBinding, BadgeVariant> = {
    ok: 'positive',
    mismatch: 'negative',
    unknown: 'negative',
    not_bound: 'base',
};

function runsOnAws(license: LicenseStatus): boolean {
    return (
        license.currentAccountId !== null ||
        license.clockSource === 'aws_time_sync'
    );
}

export function LicenseTab() {
    const { t } = useTranslation(I18nNamespace.SETTINGS);
    const { license, isLoading, error, mutate } = useLicenseStatus();
    const refresh = () => {
        void mutate();
    };

    if (isLoading) {
        return (
            <div className="flex flex-col gap-4">
                <h3 className="text-sm font-medium text-white-100">
                    {t('license.title')}
                </h3>
                <div className="border border-grey-600 rounded-[10px] p-4 text-sm text-grey-300">
                    {t('license.loading')}
                </div>
            </div>
        );
    }

    if (error || !license) {
        return (
            <div className="flex flex-col gap-4">
                <h3 className="text-sm font-medium text-white-100">
                    {t('license.title')}
                </h3>
                <div className="border border-danger-300 rounded-[5px] p-4 text-danger-200 text-sm">
                    {t('license.loadFailed')}
                </div>
            </div>
        );
    }

    if (!license.licensed || license.status === null) {
        return (
            <div className="flex flex-col gap-4">
                <h3 className="text-sm font-medium text-white-100">
                    {t('license.title')}
                </h3>
                <UnlicensedNotice licenseRequired={license.licenseRequired} />
                {runsOnAws(license) ? (
                    <AwsConnectivityRows license={license} />
                ) : (
                    <FingerprintRow fingerprint={license.machineFingerprint} />
                )}
                <ModelServicesSection services={license.modelServices ?? []} />
                <LicenseUpload onApplied={refresh} />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1">
                <h3 className="text-sm font-medium text-white-100">
                    {t('license.title')}
                </h3>
                <span className="text-xs text-grey-300">
                    {t('license.description')}
                </span>
            </div>

            <div className="flex flex-col gap-3">
                <DetailRow label={t('license.statusLabel')}>
                    <Badge variant={STATUS_VARIANTS[license.status]}>
                        {t(`license.statusValues.${license.status}`)}
                    </Badge>
                </DetailRow>
                <DetailRow label={t('license.organization')}>
                    {license.organization}
                </DetailRow>
                <DetailRow label={t('license.deployment')}>
                    {license.deployment}
                </DetailRow>
                <DetailRow label={t('license.validUntil')}>
                    {license.expiresAtEpochSec !== null
                        ? formatDate(license.expiresAtEpochSec)
                        : '—'}
                </DetailRow>
                <DetailRow label={t('license.daysRemaining')}>
                    {t('license.daysValue', {
                        count: license.daysRemaining ?? 0,
                    })}
                </DetailRow>
                <DetailRow label={t('license.cameras')}>
                    {t('license.camerasUsage', {
                        current: license.currentCameras,
                        max: license.maxCameras ?? 0,
                    })}
                </DetailRow>
                <DetailRow label={t('license.users')}>
                    {t('license.usersUsage', {
                        current: license.currentUsers,
                        max: license.maxUsers ?? 0,
                    })}
                </DetailRow>
                <DetailRow label={t('license.alertUnits')}>
                    {license.maxAlertUnits === null
                        ? t('license.unlimited')
                        : t('license.alertUnitsUsage', {
                              current: license.currentAlertUnits,
                              max: license.maxAlertUnits,
                          })}
                </DetailRow>
                <div className="border border-grey-600 rounded-[10px] p-4 flex flex-col gap-3">
                    <span className="text-sm text-grey-200">
                        {t('license.features')}
                    </span>
                    {license.features.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {license.features.map((feature) => (
                                <Badge key={feature} variant="base">
                                    {t(`license.featureNames.${feature}`, {
                                        defaultValue: feature,
                                    })}
                                </Badge>
                            ))}
                        </div>
                    ) : (
                        <span className="text-xs text-grey-300">
                            {t('license.noFeatures')}
                        </span>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-3">
                <span className="text-xs font-medium uppercase tracking-wide text-grey-300">
                    {t('license.enforcementSection')}
                </span>
                <DetailRow label={t('license.enforcement')}>
                    <Badge variant={license.hardened ? 'positive' : 'base'}>
                        {t(
                            `license.enforcementValues.${
                                license.hardened ? 'hardened' : 'configurable'
                            }`,
                        )}
                    </Badge>
                </DetailRow>
                <DetailRow label={t('license.boundTo')}>
                    {license.accountBound ? (
                        <span>
                            {t('license.bindTypes.account')}
                            {' · '}
                            <span className="font-mono text-xs text-grey-100">
                                {license.boundAccountId ?? '—'}
                            </span>
                        </span>
                    ) : license.machineBound ? (
                        <span title={license.boundMachineId ?? undefined}>
                            {t('license.bindTypes.machine')}
                            {' · '}
                            <span className="font-mono text-xs text-grey-100">
                                {license.boundMachineId
                                    ? formatFingerprint(license.boundMachineId)
                                    : '—'}
                            </span>
                        </span>
                    ) : (
                        t('license.notBound')
                    )}
                </DetailRow>
            </div>

            <div className="flex flex-col gap-3">
                <span className="text-xs font-medium uppercase tracking-wide text-grey-300">
                    {t('license.connectivitySection')}
                </span>
                <DetailRow label={t('license.bindingStatus')}>
                    {license.accountBound || license.machineBound ? (
                        <Badge
                            variant={
                                BINDING_VARIANTS[
                                    (license.accountBound
                                        ? license.accountBinding
                                        : license.machineBinding) ?? 'unknown'
                                ]
                            }
                        >
                            {t(
                                `license.bindTypes.${
                                    license.accountBound ? 'account' : 'machine'
                                }`,
                            )}
                            {' — '}
                            {t(
                                `license.bindingValues.${
                                    (license.accountBound
                                        ? license.accountBinding
                                        : license.machineBinding) ?? 'unknown'
                                }`,
                            )}
                        </Badge>
                    ) : (
                        <Badge variant="base">
                            {t('license.bindingValues.not_bound')}
                        </Badge>
                    )}
                </DetailRow>
                {license.accountBound ? (
                    <AwsConnectivityRows license={license} />
                ) : (
                    <FingerprintRow fingerprint={license.machineFingerprint} />
                )}
            </div>

            <ModelServicesSection services={license.modelServices ?? []} />

            <LicenseUpload onApplied={refresh} />
        </div>
    );
}
